/**
 * Web Audio synth — generates arcade sound effects on the fly so we don't ship
 * audio assets. Each fx is a small oscillator + ADSR envelope cooked up at call
 * time. Cheap, lazy AudioContext init (browsers require user gesture first).
 */

let ctx: AudioContext | null = null;
let muted = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx === null) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return null;
    ctx = new Ctor();
  }
  // Some browsers suspend the ctx until a user gesture. Resume on each fx.
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function setMuted(value: boolean): void {
  muted = value;
}
export function isMuted(): boolean { return muted; }

interface ToneOpts {
  freq: number;
  duration: number;       // seconds
  type?: OscillatorType;  // default 'sine'
  gain?: number;          // peak gain (0..1), default 0.18
  attack?: number;        // default 0.005s
  decay?: number;         // default duration - attack
  sweepTo?: number;       // if set, frequency sweeps linearly to this Hz
  delay?: number;         // seconds offset from now
}

function tone({
  freq,
  duration,
  type = 'sine',
  gain = 0.18,
  attack = 0.005,
  decay,
  sweepTo,
  delay = 0,
}: ToneOpts): void {
  if (muted) return;
  const ac = getCtx();
  if (ac === null) return;
  const now = ac.currentTime + delay;
  const osc = ac.createOscillator();
  const env = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (sweepTo !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, sweepTo), now + duration);
  }
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(gain, now + attack);
  env.gain.exponentialRampToValueAtTime(0.0001, now + (decay ?? duration));
  osc.connect(env).connect(ac.destination);
  osc.start(now);
  osc.stop(now + duration + 0.02);
}

// —— Public effects ——

export const sfx = {
  /** Soft click for each keystroke / tile place. */
  click(): void {
    tone({ freq: 880, duration: 0.04, type: 'square', gain: 0.05, attack: 0.001 });
  },
  /** Tile drop — slightly heavier than click. */
  tileDrop(): void {
    tone({ freq: 320, duration: 0.08, type: 'triangle', gain: 0.12, attack: 0.002 });
    tone({ freq: 480, duration: 0.06, type: 'sine', gain: 0.07, delay: 0.01 });
  },
  /** Tile pickup. */
  tilePick(): void {
    tone({ freq: 660, duration: 0.06, type: 'triangle', gain: 0.08, attack: 0.001 });
  },
  /** Perfect — major triad arpeggio sting. */
  perfect(): void {
    [523, 659, 784, 1047].forEach((f, i) => {
      tone({ freq: f, duration: 0.18, type: 'triangle', gain: 0.17, delay: i * 0.06, attack: 0.002 });
    });
    // Sparkle layer
    tone({ freq: 2093, duration: 0.4, type: 'sine', gain: 0.07, delay: 0.18 });
  },
  /** Great — clean bell ding. */
  great(): void {
    tone({ freq: 783, duration: 0.18, type: 'triangle', gain: 0.18, attack: 0.002 });
    tone({ freq: 1174, duration: 0.14, type: 'sine', gain: 0.10, delay: 0.02 });
  },
  /** Good — soft pop. */
  good(): void {
    tone({ freq: 523, duration: 0.14, type: 'sine', gain: 0.16, attack: 0.002 });
  },
  /** Miss — descending dissonant thunk. */
  miss(): void {
    tone({ freq: 220, duration: 0.28, type: 'sawtooth', gain: 0.16, sweepTo: 110, attack: 0.003 });
  },
  /** Combo level up — frequency sweep up. */
  comboUp(): void {
    tone({ freq: 440, duration: 0.22, type: 'sine', gain: 0.14, sweepTo: 1320, attack: 0.001 });
  },
  /** Combo break — chromatic split crash. */
  comboBreak(): void {
    tone({ freq: 320, duration: 0.22, type: 'sawtooth', gain: 0.12, sweepTo: 130 });
    tone({ freq: 290, duration: 0.22, type: 'sawtooth', gain: 0.10, sweepTo: 110, delay: 0.005 });
  },
};
